import { useEffect } from 'react';
import { useContextHydration } from './useContextHydration';
import { useContextStore } from './useContextStore';

/**
 * Authenticated-session context resolver (DE-336).
 *
 * Guarantees the context store is hydrated (getInit v2 + content) and an
 * `activeKitId` is resolved for EVERY authenticated session — not just when the
 * user passes through the post-login experience picker.
 *
 * Why this is needed: `activeKitId` is otherwise only set by the picker
 * (`selectConsumer`/`selectOrg`). An already-logged-in user (the common case
 * after release), a fresh tab, or a direct deep-link never hits the picker, so
 * `activeKitId` would stay `null` and disable every kit-scoped v2 query
 * (home / questionnaire / education / forethought / track).
 *
 * Resolution rules (mirror the picker's auto-redirect):
 * - consumer-only            -> auto-select consumer
 * - patient-only (>=1 org)   -> auto-select the first org
 * - both consumer AND org(s) -> leave it to the picker (BootupGuard locks to it)
 * - no kits at all           -> nothing to select (NoActiveKits downstream)
 *
 * Runs in `BootupGuard`, so it covers all protected routes. The picker keeps its
 * own hydration for the explicit-choice flow; both share the React-Query cache.
 */
export const useContextResolver = () => {
  const { isLoading, isError, orgs, consumerExists, needsPicker } =
    useContextHydration();
  const hasSelectedContext = useContextStore((s) => s.hasSelectedContext);
  const selectConsumer = useContextStore((s) => s.selectConsumer);
  const selectOrg = useContextStore((s) => s.selectOrg);

  useEffect(() => {
    if (isLoading || isError || hasSelectedContext) return;
    // Both experiences exist -> the user must pick; don't auto-select.
    if (consumerExists && orgs.length >= 1) return;
    // Exactly one experience -> auto-select so activeKitId is populated.
    if (orgs.length >= 1) {
      selectOrg(orgs[0].org.id);
    } else if (consumerExists) {
      selectConsumer();
    }
  }, [
    isLoading,
    isError,
    hasSelectedContext,
    consumerExists,
    orgs,
    selectConsumer,
    selectOrg,
  ]);

  const hasNoKits = !consumerExists && orgs.length === 0;

  // Ready once we've auto-selected, determined the picker is required, or
  // confirmed there are no kits — so BootupGuard never renders home with a null
  // activeKitId. On error we also unblock: context resolution is best-effort, so
  // the app falls through to useBootup's v1 getInit fallback rather than hard-
  // failing every authenticated user on a v2 hiccup.
  const isReady =
    !isLoading && (hasSelectedContext || needsPicker || hasNoKits || isError);

  return { isLoading, isError, isReady, needsPicker, hasNoKits };
};
